<template>
  <nav
    class="app-sidebar"
    :class="{
      'app-sidebar--mobile': mobile,
      'app-sidebar--open': modelValue,
      'app-sidebar--collapsed': !mobile && !modelValue
    }"
  >
    <!-- Mobile Overlay (click to close sidebar) -->
    <!-- Using position: fixed with inset: 0 to cover entire viewport including header -->
    <div
      class="app-sidebar__overlay"
      :class="{ 'app-sidebar__overlay--visible': modelValue }"
      @click.stop="$emit('update:modelValue', false)"
      @touchstart.stop.prevent="$emit('update:modelValue', false)"
      aria-label="Close sidebar"
      role="button"
      tabindex="0"
    >
      <!-- Overlay content (required for Vue to not strip this element) -->
      <span class="sr-only">Tap to close sidebar</span>
    </div>
    <!-- Sidebar content -->
    <aside
      class="app-sidebar__content"
      :class="{ 'app-sidebar__content--visible': modelValue }"
    >
      <!-- Create buttons - moved to top -->
      <div class="app-sidebar__create-buttons">
        <button class="app-sidebar__create-btn" @click="handlePrimaryCreate">
          <v-icon size="20">{{ primaryCreateIcon }}</v-icon>
          <span>{{ primaryCreateLabel }}</span>
        </button>
        <button
          v-if="showSecondaryCreate"
          class="app-sidebar__upload-btn"
          @click="handleSecondaryCreate"
        >
          <v-icon size="20">{{ secondaryCreateIcon }}</v-icon>
          <span>{{ secondaryCreateLabel }}</span>
        </button>
      </div>

      <!-- Primary Navigation (mode-specific) -->
      <div class="app-sidebar__nav">
        <template v-for="item in primaryNavItems" :key="item.to">
          <a
            v-if="item.href"
            :href="item.href"
            class="app-sidebar__nav-item"
          >
            <div class="app-sidebar__nav-icon">
              <v-icon :icon="item.icon" size="22" />
            </div>
            <span class="app-sidebar__nav-label">{{ item.title }}</span>
          </a>
          <router-link
            v-else
            :to="item.to"
            class="app-sidebar__nav-item"
            :class="{ 'app-sidebar__nav-item--active': isActive(item.to, item.exact) }"
          >
            <div class="app-sidebar__nav-icon">
              <v-icon
                :icon="
                  isActive(item.to, item.exact) ? item.iconActive || item.icon : item.icon
                "
                size="22"
              />
            </div>
            <span class="app-sidebar__nav-label">{{ item.title }}</span>
            <span v-if="item.badge" class="app-sidebar__nav-badge">{{
              item.badge
            }}</span>
          </router-link>
        </template>
      </div>

      <!-- Secondary Navigation (mode-specific) -->
      <div v-if="secondaryNavItems.length > 0" class="app-sidebar__nav">
          <router-link
            v-for="item in secondaryNavItems"
            :key="item.to"
            :to="item.to"
            class="app-sidebar__nav-item"
            :class="{ 'app-sidebar__nav-item--active': isActive(item.to, item.exact) }"
          >
            <div class="app-sidebar__nav-icon">
              <v-icon
                :icon="
                  isActive(item.to, item.exact) ? item.iconActive || item.icon : item.icon
                "
                size="22"
              />
            </div>
            <span class="app-sidebar__nav-label">{{ item.title }}</span>
            <span v-if="item.badge" class="app-sidebar__nav-badge">{{
              item.badge
            }}</span>
          </router-link>
      </div>

      <!-- Utility Navigation -->
      <div v-if="utilityNavItems.length > 0" class="app-sidebar__nav">
        <router-link
          v-for="item in utilityNavItems"
          :key="item.to"
          :to="item.to"
          class="app-sidebar__nav-item"
          :class="{
            'app-sidebar__nav-item--active': isActive(item.to, item.exact),
          }"
        >
          <div class="app-sidebar__nav-icon">
            <v-icon
              :icon="
                isActive(item.to, item.exact) ? item.iconActive || item.icon : item.icon
              "
              size="22"
            />
          </div>
          <span class="app-sidebar__nav-label">{{ item.title }}</span>
          <span
            v-if="item.badge"
            class="app-sidebar__nav-badge"
          >
            {{ item.badge }}
          </span>
        </router-link>
      </div>

      <!-- Spacer -->
      <div class="app-sidebar__spacer" />

      <!-- Bottom section -->
      <div class="app-sidebar__bottom">
        <div class="app-sidebar__divider" />

        <!-- Website Link (if configured) -->
        <button
          v-if="siteWebsite"
          class="app-sidebar__nav-item"
          @click="openWebsite"
        >
          <div class="app-sidebar__nav-icon">
            <v-icon icon="mdi-open-in-new" size="22" />
          </div>
          <span class="app-sidebar__nav-label">Website</span>
        </button>

        <router-link
          v-if="!isAuthenticated"
          to="/login"
          class="app-sidebar__nav-item app-sidebar__nav-item--highlight"
        >
          <div class="app-sidebar__nav-icon">
            <v-icon icon="mdi-login" size="22" />
          </div>
          <span class="app-sidebar__nav-label">Sign In</span>
        </router-link>

        <!-- User card (when authenticated) -->
        <div v-if="isAuthenticated && user" class="app-sidebar__user-card">
          <router-link :to="profileLink" class="app-sidebar__user-link" @click="closeSidebar">
            <div class="app-sidebar__user-avatar">
              <v-img v-if="userAvatar" :src="userAvatar" :alt="user.name" cover />
              <span v-else>{{ user.name?.charAt(0).toUpperCase() || "U" }}</span>
            </div>
            <div class="app-sidebar__user-info">
              <span class="app-sidebar__user-name">{{
                user.name || "User"
              }}</span>
              <span class="app-sidebar__user-handle"
                >@{{ getDisplayHandle(user) }}</span
              >
            </div>
          </router-link>
          <!-- Profile Menu Overlay -->
          <div
            v-if="isProfileMenuOpen"
            class="app-sidebar__profile-menu-overlay"
            @click.stop="isProfileMenuOpen = false"
            @touchstart.stop.prevent="isProfileMenuOpen = false"
            aria-label="Close profile menu"
            role="button"
            tabindex="0"
          >
            <span class="sr-only">Tap to close profile menu</span>
          </div>
          <v-menu v-model="isProfileMenuOpen" location="top end" :close-on-content-click="true" :z-index="6000">
            <template #activator="{ props: menuProps }">
              <button
                v-bind="menuProps"
                class="app-sidebar__user-more-btn"
                aria-label="User menu"
              >
                <v-icon size="16">mdi-dots-horizontal</v-icon>
              </button>
            </template>
            <div class="app-sidebar__user-menu">
              <!-- User info header -->
              <div class="app-sidebar__menu-user">
                <div class="app-sidebar__menu-avatar">
                  <v-img v-if="userAvatar" :src="userAvatar" :alt="user?.name" cover />
                  <span v-else>{{ user?.name?.charAt(0).toUpperCase() || "U" }}</span>
                </div>
                <div class="app-sidebar__menu-user-info">
                  <span class="app-sidebar__menu-name">{{ user?.displayName || user?.name || user?.username || "User" }}</span>
                  <span class="app-sidebar__menu-handle">@{{ getDisplayHandle(user) }}</span>
                </div>
              </div>

              <div class="app-sidebar__menu-divider" />

              <button class="app-sidebar__menu-item" @click="goToProfile">
                <v-icon size="18">mdi-account-outline</v-icon>
                <span>Profile</span>
              </button>
              <button class="app-sidebar__menu-item" @click="goToAnalytics">
                <v-icon size="18">mdi-chart-areaspline</v-icon>
                <span>Analytics</span>
              </button>
              <button v-if="hasAdministratorBadge" class="app-sidebar__menu-item" @click="goToAdminDashboard">
                <v-icon size="18">mdi-view-dashboard-outline</v-icon>
                <span>Admin Dashboard</span>
              </button>
              <button class="app-sidebar__menu-item" @click="goToSettings">
                <v-icon size="18">mdi-cog-outline</v-icon>
                <span>Settings</span>
              </button>
              <button class="app-sidebar__menu-item" @click="goToManageUsers">
                <v-icon size="18">mdi-account-group</v-icon>
                <span>Manage Users</span>
              </button>

              <div class="app-sidebar__menu-divider" />

              <button class="app-sidebar__menu-item" @click="handleOpenTerms">
                <v-icon size="18">mdi-file-document-outline</v-icon>
                <span>Terms of Service</span>
              </button>
              <button class="app-sidebar__menu-item" @click="handleOpenPrivacy">
                <v-icon size="18">mdi-shield-lock-outline</v-icon>
                <span>Privacy Policy</span>
              </button>
              <button class="app-sidebar__menu-item" @click="goToHelp">
                <v-icon size="18">mdi-help-circle-outline</v-icon>
                <span>Help Center</span>
              </button>

              <div class="app-sidebar__menu-divider" />

              <button class="app-sidebar__menu-item app-sidebar__menu-item--danger" @click="handleLogout">
                <v-icon size="18">mdi-logout</v-icon>
                <span>Sign out</span>
              </button>
            </div>
          </v-menu>
        </div>

        <!-- Footer Links -->
        <nav class="app-sidebar__footer-links">
          <a href="#" @click.prevent="handleOpenTerms">Terms</a>
          <span class="app-sidebar__footer-dot">·</span>
          <a href="#" @click.prevent="handleOpenPrivacy">Privacy</a>
          <span class="app-sidebar__footer-dot">·</span>
          <router-link to="/help" @click="closeSidebar">Help</router-link>
        </nav>

        <!-- App Version -->
        <div class="app-sidebar__version">
          <span>&copy; {{ new Date().getFullYear() }} {{ companyName }}</span>
          <span class="app-sidebar__version-number">{{ appVersion }}</span>
        </div>
      </div>
    </aside>
  </nav>
</template>

<script setup lang="ts">
import { computed, inject, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { userHasRole } from "@cc-consulting-nv/ccsdk";
import { useAuth } from "@/composables/useAuth";
import { useAppSettings } from "@/composables/useAppSettings";
import { useChatGroups } from "@/queries/useMessagesQueries";

// Inject create functions from App.vue
const openCreatePost = inject<() => void>("openCreatePost");
const openCreateSong = inject<() => void>("openCreateSong");

interface Props {
  modelValue?: boolean;
  rail?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: true,
  rail: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  "open-settings": [];
  "open-terms": [];
  "open-privacy": [];
}>();

// Check if user has administrator badge (same logic as AppHeader)
const hasAdministratorBadge = computed(() => {
  return user.value?.badges?.includes('administrator') ?? false;
});

// Check if user has crm* role (crm_user, crm_admin) - gates CRM menu
const hasCrmRole = computed(() => userHasRole(user.value, "crm"));

// Check if user has Beta Tester badge (gates WSOM access)
const hasBetaTesterBadge = computed(() => currentUserHasBadge('Beta Tester'));

const route = useRoute();
const router = useRouter();
const { mobile } = useDisplay();
const { user, isAuthenticated, logout, currentUserHasBadge, getDisplayHandle } = useAuth();
const { siteWebsite, companyName, isMusicMode, featureMessagingEnabled, uiSettings } = useAppSettings();

// Track profile menu open state
const isProfileMenuOpen = ref(false);

// Auto-close profile menu when sidebar closes
watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) {
      isProfileMenuOpen.value = false;
    }
  },
);

// Auto-close sidebar on mobile when route changes
watch(
  () => route.path,
  () => {
    if (mobile.value && props.modelValue) {
      emit("update:modelValue", false);
    }
  },
);

// App version from environment variable
const appVersion = computed(() => {
  return import.meta.env.VITE_APP_VERSION || "0.0.1";
});

// Chat groups for unread count - updates are received via real-time WebSocket events
// Only fetch if messaging feature is enabled
const { groups: chatGroups } = useChatGroups({
  enabled: computed(() => isAuthenticated.value && featureMessagingEnabled.value),
});

// Track unread message count and pulse animation
const totalUnreadMessages = computed(() => {
  return chatGroups.value.reduce(
    (total, group) => total + (group.unreadCount || 0),
    0,
  );
});

// Pulse animation state - triggers when new message arrives
const hasNewMessage = ref(false);
let pulseTimeout: ReturnType<typeof setTimeout> | null = null;

watch(totalUnreadMessages, (newCount, oldCount) => {
  if (newCount > oldCount) {
    // New message arrived - trigger pulse
    hasNewMessage.value = true;
    if (pulseTimeout) clearTimeout(pulseTimeout);
    pulseTimeout = setTimeout(() => {
      hasNewMessage.value = false;
    }, 2000); // Pulse for 2 seconds
  }
});

interface NavItem {
  to: string;
  icon: string;
  iconActive?: string;
  title: string;
  badge?: number;
  exact?: boolean;
  href?: string;
}

// Tab persistence: Store last visited discover category (excluding timeline which has its own menu item)
const lastDiscoverTab = ref<string>('songs');

// Watch route changes to remember discover tab
watch(
  () => route.path,
  (newPath) => {
    if (newPath.startsWith('/discover/')) {
      const category = newPath.split('/')[2];
      // Exclude timeline since it has its own menu item
      if (category && ['trending', 'songs'].includes(category)) {
        lastDiscoverTab.value = category;
      }
    }
  },
  { immediate: true }
);

// Unified primary navigation
const primaryNavItems = computed<NavItem[]>(() => {
  const items: NavItem[] = [];

  // Music-only items
  if (isMusicMode.value) {
    items.push(
      {
        to: "/explore",
        icon: "mdi-playlist-music-outline",
        iconActive: "mdi-playlist-music",
        title: "Explore",
      },
      {
        to: "/listen",
        icon: "mdi-headphones",
        iconActive: "mdi-headphones",
        title: "Listen",
      },
      {
        to: "/radio-stations",
        icon: "mdi-radio",
        iconActive: "mdi-radio",
        title: "Radio Stations",
      }
    );
  }

  // Items shown in all modes
  items.push(
    {
      to: "/timeline",
      icon: "mdi-timeline-text-outline",
      iconActive: "mdi-timeline-text",
      title: "Timeline",
    },
    { to: "/search", icon: "mdi-magnify", title: "Search" }
  );

  // CRM for users with crm* role (Filament panel — full page nav)
  if (isAuthenticated.value && hasCrmRole.value) {
    items.push({
      to: "/crm",
      href: "/crm",
      icon: "mdi-account-tie-outline",
      iconActive: "mdi-account-tie",
      title: "CRM",
    });
  }

  // Music-only: WSOM Contest (visible to all) + Events (beta_tester only)
  if (isMusicMode.value && uiSettings.value.wsomContest === 'true') {
    items.push({
      to: "/wsom",
      icon: "mdi-trophy-outline",
      iconActive: "mdi-trophy",
      title: "WSOM Contest",
      exact: true,
    });

    if (hasBetaTesterBadge.value) {
      items.push({
        to: "/wsom/events",
        icon: "mdi-calendar-outline",
        iconActive: "mdi-calendar",
        title: "WSOM Events",
      });
    }
  }

  return items;
});


// Secondary navigation - Your Library (music-only items)
const secondaryNavItems = computed<NavItem[]>(() => {
  if (!isMusicMode.value) {
    return [];
  }
  return [
    {
      to: "/made-for-you",
      icon: "mdi-headphones",
      iconActive: "mdi-headphones",
      title: "Made For You",
    },
    {
      to: "/rate",
      icon: "mdi-star-shooting-outline",
      iconActive: "mdi-star-shooting",
      title: "Rate Songs",
    },
  ];
});

// Utility navigation items (shown to all users)
const utilityNavItems = computed<NavItem[]>(() => {
  const items: NavItem[] = [
    {
      to: "/groups",
      icon: "mdi-account-group-outline",
      iconActive: "mdi-account-group",
      title: "Groups",
    },
  ];

  // Moderation link for administrators/moderators
  if (isAuthenticated.value && hasAdministratorBadge.value) {
    items.push({
      to: "/moderation",
      icon: "mdi-shield-check-outline",
      iconActive: "mdi-shield-check",
      title: "Moderation",
    });
  }

  if (isAuthenticated.value) {
    items.push({
      to: "/settings/feedback",
      icon: "mdi-comment-question-outline",
      iconActive: "mdi-comment-question",
      title: "Feedback",
    });
  }

  return items;
});

// Create buttons
const primaryCreateIcon = computed(() => "mdi-plus");
const primaryCreateLabel = computed(() => "Post");
const showSecondaryCreate = computed(() => isMusicMode.value);
const secondaryCreateIcon = computed(() => "mdi-music-note-plus");
const secondaryCreateLabel = computed(() => "Upload Song");

const profileLink = computed(() => {
  return user.value ? `/profile/${user.value.username}` : "/profile";
});

// Computed avatar that handles different field names from API
const userAvatar = computed(() => {
  if (!user.value) return null;
  const u = user.value as Record<string, unknown>;
  return (u.avatar || u.avatarUrl || u.avatar_url || u.profile_image) as
    | string
    | null;
});

function isActive(path: string, exact = false): boolean {
  if (path === "/" || exact) {
    return route.path === path;
  }
  return route.path.startsWith(path);
}

function handleLogout() {
  closeSidebar();
  logout();
}

function closeSidebar() {
  if (mobile.value) {
    emit("update:modelValue", false);
  }
}

function handleOpenSettings() {
  closeSidebar();
  emit("open-settings");
}

function handleOpenTerms() {
  closeSidebar();
  emit("open-terms");
}

function handleOpenPrivacy() {
  closeSidebar();
  emit("open-privacy");
}

function goToProfile() {
  closeSidebar();
  router.push(profileLink.value);
}

function goToAnalytics() {
  closeSidebar();
  router.push("/analytics");
}

function goToAdminDashboard() {
  closeSidebar();
  router.push("/admin");
}

function goToManageUsers() {
  closeSidebar();
  router.push("/admin/users");
}

function goToSettings() {
  closeSidebar();
  router.push("/settings");
}

function goToHelp() {
  closeSidebar();
  router.push("/help");
}

function handlePrimaryCreate() {
  openCreatePost?.();
  closeSidebar();
}

function handleSecondaryCreate() {
  openCreateSong?.();
  closeSidebar();
}

function openWebsite() {
  if (siteWebsite.value) {
    window.open(siteWebsite.value, "_blank", "noopener,noreferrer");
  }
}

// Clean up pulse timeout on unmount
onUnmounted(() => {
  if (pulseTimeout) {
    clearTimeout(pulseTimeout);
    pulseTimeout = null;
  }
});

</script>

<style scoped lang="scss">
.app-sidebar {
  // Define header height including safe area for consistency
  --header-height: calc(64px + env(safe-area-inset-top, 0px));

  position: fixed;
  top: var(--header-height); // Account for header + notch/Dynamic Island
  left: 0;
  bottom: 0;
  z-index: 900;
  width: 260px;

  // Align sidebar with the centered container whenever the container is centered
  // Match the header breakpoint at 1360px - when viewport >= 1360px, container centers
  // app-max-width = 260px (sidebar) + 1100px (content) = 1360px
  @media (min-width: 1360px) {
    // Calculate left offset to align with container's left edge
    // When container is centered: left margin = (100vw - app-max-width) / 2
    // Sidebar should align with container's left edge, so use the same calculation
    left: calc((100vw - var(--app-max-width)) / 2);
  }

  // Desktop collapsed state - show only icons
  &--collapsed {
    width: var(--sidebar-collapsed-width, 72px);
    transition: width 0.3s var(--ease-out-expo);

    .app-sidebar__content {
      width: var(--sidebar-collapsed-width, 72px);
      padding: 16px 8px;
      transform: translateX(0);
      transition: width 0.3s var(--ease-out-expo), padding 0.3s var(--ease-out-expo);
    }

    // Hide labels, badges, and section titles in collapsed mode
    .app-sidebar__nav-label,
    .app-sidebar__nav-badge,
    .app-sidebar__section-title,
    .app-sidebar__create-btn span,
    .app-sidebar__upload-btn span,
    .app-sidebar__user-info,
    .app-sidebar__user-more-btn,
    .app-sidebar__footer-links,
    .app-sidebar__version {
      opacity: 0;
      width: 0;
      overflow: hidden;
      transition: opacity 0.2s ease, width 0.2s ease;
    }

    // Center icons in nav items
    .app-sidebar__nav-item {
      justify-content: center;
      padding: 12px;
      gap: 0;
    }

    // Make create buttons icon-only circles
    .app-sidebar__create-btn,
    .app-sidebar__upload-btn {
      width: 48px;
      height: 48px;
      padding: 0;
      border-radius: 50%;
      justify-content: center;
      gap: 0;
    }

    .app-sidebar__create-buttons {
      align-items: center;
    }

    // Collapse user card to just avatar
    .app-sidebar__user-card {
      justify-content: center;
      padding: 8px;
    }

    .app-sidebar__user-link {
      justify-content: center;
    }

    // Hide section dividers in collapsed mode
    .app-sidebar__section {
      border-top: none;
      padding-top: 8px;
      margin-top: 8px;
    }

    // Hide bottom section divider
    .app-sidebar__divider {
      display: none;
    }

    // Hide spacer (not needed in collapsed)
    .app-sidebar__spacer {
      min-height: 8px;
    }
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  // Desktop expanded state
  &:not(&--mobile)#{&}--open {
    .app-sidebar__content {
      transform: translateX(0);
      transition: transform 0.3s var(--ease-out-expo);
    }
  }

  // iOS PWA / standalone mode - ensure proper safe area handling
  @supports (padding-top: env(safe-area-inset-top)) {
    @media (display-mode: standalone) {
      --header-height: calc(64px + env(safe-area-inset-top, 47px));
    }
  }

  &--mobile {
    // On mobile, sidebar starts off-screen and slides in when opened
    // Position above everything including header
    top: 0;
    z-index: 5000;
    width: 280px; // Fixed width, not 100% - overlay handles the rest
    // CRITICAL: Don't capture pointer events on the nav wrapper when closed
    pointer-events: none;

    .app-sidebar__content {
      // Account for safe area at top and bottom on mobile (iOS notch/Dynamic Island and home indicator)
      padding-top: calc(20px + env(safe-area-inset-top, 0px));
      padding-bottom: calc(120px + env(safe-area-inset-bottom, 0px));
      pointer-events: auto; // Re-enable pointer events on content
      // Use dvh for proper height on iOS Safari (accounts for dynamic toolbar)
      height: 100dvh;
      max-height: 100dvh;
    }

    // When open, allow pointer events on the whole sidebar area
    &.app-sidebar--open {
      pointer-events: auto;
    }
  }

  // Mode Switcher - Premium glass morphism design
  &__mode-switcher {
    margin-bottom: 20px;
  }

  &__mode-track {
    position: relative;
    display: flex;
    padding: 4px;
    background: linear-gradient(
      135deg,
      rgba(var(--v-theme-on-surface), 0.03) 0%,
      rgba(255, 255, 255, 0.01) 100%
    );
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 14px;
    backdrop-filter: blur(8px);
    touch-action: none; // Disable browser touch handling to capture all touch events
    user-select: none; // Prevent text selection during swipe
    -webkit-user-select: none;
    -webkit-touch-callout: none; // Disable iOS callout
  }

  &__mode-indicator {
    position: absolute;
    top: 4px;
    height: 36px;
    background: linear-gradient(
      135deg,
      rgba(var(--v-theme-primary), 0.2) 0%,
      rgba(var(--v-theme-primary), 0.12) 100%
    );
    border: 1px solid rgba(var(--v-theme-primary), 0.25);
    border-radius: 10px;
    box-shadow:
      0 4px 12px rgba(var(--v-theme-primary), 0.15),
      inset 0 1px 0 rgba(var(--v-theme-on-surface), 0.1);
    transition:
      left 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
      width 0.35s ease;
    pointer-events: none;
    z-index: 0;
  }

  &__mode-btn {
    position: relative;
    z-index: 1;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 68px;
    height: 36px;
    border: none;
    background: transparent;
    border-radius: 10px;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
    transition: all 0.25s ease;

    .v-icon {
      transition:
        transform 0.25s ease,
        color 0.25s ease;
    }

    &:hover:not(&--active) {
      color: rgba(var(--v-theme-on-surface), 0.75);

      .v-icon {
        transform: scale(1.1);
      }
    }

    &--active {
      color: rgb(var(--v-theme-primary));

      .v-icon {
        transform: scale(1.15);
        filter: drop-shadow(0 2px 4px rgba(var(--v-theme-primary), 0.4));
      }
    }
  }

  // Overlay (matches search modal overlay pattern)
  &__overlay {
    position: fixed;
    top: 0;
    left: 280px; // Start after sidebar (sidebar width on mobile)
    right: 0;
    bottom: 0;
    background: rgba(var(--v-theme-overlay), 0.7);
    backdrop-filter: blur(4px);
    opacity: 0;
    z-index: 3003; // Above header (3002) but still allows sidebar (3001) to be interactive
    transition: opacity 0.3s var(--ease-out-expo);
    pointer-events: none;
    cursor: pointer;
    touch-action: none; // Prevent any default touch behavior

    // Only show on mobile (< 1280px)
    @media (min-width: 1280px) {
      display: none !important;
    }

    &--visible {
      opacity: 1;
      pointer-events: auto;
    }
  }

  // Content
  &__content {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 20px 16px;
    padding-bottom: 120px; // Extra padding for audio player
    background: rgba(12, 10, 9, 0.5);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-right: 1px solid rgba(255, 255, 255, 0.06);
    overflow-y: auto;
    pointer-events: auto;
    position: relative;
    z-index: 3004; // Highest z-index - sidebar must be above overlay and header

    // On desktop, sidebar is always visible (controlled by v-model in App.vue)
    // On mobile, sidebar slides in from the left
    .app-sidebar--mobile & {
      width: 280px;
      box-shadow: 4px 0 24px rgba(0, 0, 0, 0.3);
      transform: translateX(-100%);
      transition: transform 0.3s var(--ease-out-expo);
    }

    .app-sidebar--mobile &--visible {
      transform: translateX(0);
    }
  }

  // Section
  &__section {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  &__section-title {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(var(--v-theme-on-surface), 0.5);
    padding: 0 16px 8px;
    margin: 0;
  }

  // Navigation
  &__nav {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__nav-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    border: none;
    background: none;
    border-radius: 14px;
    text-decoration: none;
    font-family: var(--font-body);
    font-size: 0.9375rem;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
    transition: all 0.2s var(--ease-out-expo);
    width: 100%;
    text-align: left;

    &:hover {
      background: rgba(255, 255, 255, 0.06);
      color: rgb(var(--v-theme-on-surface));
    }

    &--active {
      background: rgba(var(--v-theme-primary), 0.1);
      color: rgb(var(--v-theme-on-surface));

      .app-sidebar__nav-icon {
        color: rgb(var(--v-theme-primary));
      }

      &:hover {
        background: rgba(var(--v-theme-primary), 0.15);
      }
    }

    &--danger {
      color: rgba(var(--v-theme-on-surface), 0.6);

      &:hover {
        background: rgba(var(--v-theme-error), 0.1);
        color: rgb(var(--v-theme-error));

        .app-sidebar__nav-icon {
          color: rgb(var(--v-theme-error));
        }
      }
    }

    &--highlight {
      background: linear-gradient(
        135deg,
        rgba(var(--v-theme-primary), 0.15),
        rgba(var(--v-theme-primary), 0.05)
      );
      color: rgb(var(--v-theme-primary));

      &:hover {
        background: linear-gradient(
          135deg,
          rgba(var(--v-theme-primary), 0.2),
          rgba(var(--v-theme-primary), 0.1)
        );
      }
    }
  }

  &__nav-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    color: inherit;
  }

  &__nav-label {
    flex: 1;
  }

  &__nav-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    background: rgb(var(--v-theme-primary));
    border-radius: 50px;
    font-size: 0.6875rem;
    font-weight: 700;
    color: rgb(var(--v-theme-on-primary, 12 10 9));
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease;

    &--pulse {
      animation: badge-pulse 0.6s ease-in-out infinite;
      box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.7);
    }
  }

  // Pulse animation for new messages
  &__nav-item--pulse {
    .app-sidebar__nav-icon {
      animation: icon-shake 0.5s ease-in-out;
    }
  }

  @keyframes badge-pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.7);
    }
    50% {
      transform: scale(1.15);
      box-shadow: 0 0 0 8px rgba(var(--v-theme-primary), 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0);
    }
  }

  @keyframes icon-shake {
    0%,
    100% {
      transform: translateX(0) rotate(0);
    }
    20% {
      transform: translateX(-2px) rotate(-5deg);
    }
    40% {
      transform: translateX(2px) rotate(5deg);
    }
    60% {
      transform: translateX(-2px) rotate(-3deg);
    }
    80% {
      transform: translateX(2px) rotate(3deg);
    }
  }

  // Create buttons container
  &__create-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 16px;
    margin-bottom: 20px;
  }

  // Create button
  &__create-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    height: 48px;
    border: none;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)) 0%,
      rgb(var(--v-theme-secondary)) 100%
    );
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.9375rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-primary, 12 10 9));
    cursor: pointer;
    transition: all 0.3s var(--ease-out-expo);
    box-shadow: 0 4px 16px rgba(var(--v-theme-primary), 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(var(--v-theme-primary), 0.4);
    }

    &:active {
      transform: translateY(0);
    }
  }

  // Upload music button
  &__upload-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    height: 44px;
    border: 1px solid rgba(var(--v-theme-primary), 0.3);
    background: rgba(var(--v-theme-primary), 0.08);
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.875rem;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
    transition: all 0.3s var(--ease-out-expo);

    &:hover {
      background: rgba(var(--v-theme-primary), 0.15);
      border-color: rgba(var(--v-theme-primary), 0.5);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  // Spacer
  &__spacer {
    flex: 1;
    min-height: 24px;
  }

  // Bottom section
  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__divider {
    height: 1px;
    margin: 8px 0 12px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(var(--v-theme-on-surface), 0.08),
      transparent
    );
  }

  // User card (hidden on desktop, shown on mobile only)
  &__user-card {
    display: none; // Hide on desktop
    align-items: center;
    gap: 12px;
    padding: 12px;
    margin-top: 12px;
    background: rgba(var(--v-theme-on-surface), 0.03);
    border-radius: 14px;
    cursor: pointer;
    transition: all 0.2s ease;

    // Show on mobile
    .app-sidebar--mobile & {
      display: flex;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.06);
    }
  }

  &__user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)),
      rgb(var(--v-theme-secondary))
    );
    padding: 2px;
    flex-shrink: 0;

    .v-img,
    span {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: rgba(var(--v-theme-on-surface), 0.08);
      font-size: 0.875rem;
      font-weight: 600;
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__user-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  &__user-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__user-handle {
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__user-link {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
    text-decoration: none;
  }

  &__user-more-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    border-radius: 8px;
    color: rgba(var(--v-theme-on-surface), 0.5);
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.08);
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__profile-menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(var(--v-theme-overlay), 0.7);
    backdrop-filter: blur(4px);
    z-index: 5999; // Just below the menu (6000)
    cursor: pointer;
    touch-action: none; // Prevent any default touch behavior

    // Only show on mobile (< 1280px)
    @media (min-width: 1280px) {
      display: none !important;
    }
  }

  &__user-menu {
    min-width: 220px;
    padding: 12px;
    background: rgba(28, 25, 23, 0.98);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  }

  &__menu-user {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 12px;
    background: rgba(var(--v-theme-on-surface), 0.03);
  }

  &__menu-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)),
      rgb(var(--v-theme-secondary))
    );
    padding: 2px;
    flex-shrink: 0;

    .v-img,
    span {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: rgba(var(--v-theme-on-surface), 0.08);
      font-weight: 600;
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__menu-user-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__menu-name {
    font-weight: 600;
    font-size: 0.9375rem;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__menu-handle {
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }

  &__menu-divider {
    height: 1px;
    margin: 8px 4px;
    background: rgba(255, 255, 255, 0.06);
  }

  &__menu-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px;
    border: none;
    background: none;
    border-radius: 10px;
    font-family: var(--font-body);
    font-size: 0.9375rem;
    color: rgb(var(--v-theme-on-surface));
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;

    .v-icon {
      color: inherit;
    }

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.06);
      color: rgb(var(--v-theme-on-surface));
    }

    &--danger {
      color: rgb(var(--v-theme-error));

      &:hover {
        background: rgba(var(--v-theme-error), 0.1);
        color: rgb(var(--v-theme-error));
      }
    }
  }

  // Footer links
  &__footer-links {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    padding: 8px 16px;

    a {
      font-size: 0.8125rem;
      color: rgba(var(--v-theme-on-surface), 0.5);
      text-decoration: none;
      transition: color 0.2s ease;

      &:hover {
        color: rgb(var(--v-theme-on-surface));
        text-decoration: underline;
      }
    }
  }

  &__footer-dot {
    color: rgba(var(--v-theme-on-surface), 0.3);
  }

  // Version display
  &__version {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 16px;
    margin-top: 4px;
    font-size: 0.6875rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }

  &__version-number {
    font-family: var(--font-mono, monospace);
    font-size: 0.625rem;
    color: rgba(var(--v-theme-on-surface), 0.4);
  }
}

// Light theme
.v-theme--light {
  .app-sidebar {
    &__content {
      background: rgba(250, 250, 249, 0.6);
      border-right-color: rgba(0, 0, 0, 0.06);
    }

    &__mode-track {
      background: linear-gradient(
        135deg,
        rgba(var(--v-theme-on-surface), 0.03) 0%,
        rgba(0, 0, 0, 0.01) 100%
      );
      border-color: rgba(0, 0, 0, 0.06);
    }

    &__mode-indicator {
      background: linear-gradient(
        135deg,
        rgba(var(--v-theme-primary), 0.15) 0%,
        rgba(var(--v-theme-primary), 0.08) 100%
      );
      border-color: rgba(var(--v-theme-primary), 0.2);
      box-shadow:
        0 4px 12px rgba(var(--v-theme-primary), 0.1),
        inset 0 1px 0 rgba(var(--v-theme-on-surface), 0.5);
    }

    &__mode-btn {
      color: rgba(var(--v-theme-on-surface), 0.55);

      &:hover:not(.app-sidebar__mode-btn--active) {
        color: rgba(var(--v-theme-on-surface), 0.7);
      }

      &--active {
        color: rgb(var(--v-theme-primary));

        .v-icon {
          filter: drop-shadow(0 2px 4px rgba(var(--v-theme-primary), 0.3));
        }
      }
    }

    &__nav-item {
      color: rgba(var(--v-theme-on-surface), 0.4);

      &:hover {
        background: rgba(0, 0, 0, 0.04);
        color: rgb(var(--v-theme-on-surface));
      }

      &--active {
        background: rgba(var(--v-theme-primary), 0.08);
        color: rgb(var(--v-theme-on-surface));

        &:hover {
          background: rgba(var(--v-theme-primary), 0.12);
        }
      }

      &--highlight {
        background: linear-gradient(
          135deg,
          rgba(var(--v-theme-primary), 0.12),
          rgba(var(--v-theme-primary), 0.04)
        );
        color: rgb(var(--v-theme-primary));
      }
    }

    &__section {
      border-top-color: rgba(0, 0, 0, 0.06);
    }

    &__section-title {
      color: rgba(var(--v-theme-on-surface), 0.6);
    }

    &__upload-btn {
      border-color: rgba(var(--v-theme-primary), 0.3);
      background: rgba(var(--v-theme-primary), 0.08);
      color: rgb(var(--v-theme-primary));

      &:hover {
        background: rgba(var(--v-theme-primary), 0.15);
        border-color: rgba(var(--v-theme-primary), 0.5);
      }
    }

    &__divider {
      background: linear-gradient(
        90deg,
        transparent,
        rgba(var(--v-theme-on-surface), 0.08),
        transparent
      );
    }

    &__user-card {
      background: rgba(var(--v-theme-on-surface), 0.03);

      &:hover {
        background: rgba(var(--v-theme-on-surface), 0.05);
      }
    }

    &__user-avatar {
      span {
        background: rgba(var(--v-theme-on-surface), 0.04);
        color: rgb(var(--v-theme-on-surface));
      }
    }

    &__user-name {
      color: rgb(var(--v-theme-on-surface));
    }

    &__menu-item {
      color: rgb(var(--v-theme-on-surface));

      .v-icon {
        color: inherit;
      }

      &:hover {
        background: rgba(0, 0, 0, 0.04);
        color: rgb(var(--v-theme-on-surface));
      }
    }
  }
}
</style>
